/* eslint-disable react-hooks/immutability */
import { type TableOptions } from '@tanstack/react-table';

import { normalizeThresholdRules } from './normalize-rules.js';
import type { DataTableThresholdProps } from './threshold-types.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * Hook configuring the thresholds feature for the DataTable.
 * @internal
 */
export function useThresholds<
  TFeatures extends TDataTableFeatures,
  TData extends DataTableRowData,
>(
  props: DataTableThresholdProps<TData>,
  options: TableOptions<TFeatures, Exclude<TData, unknown>>,
): void {
  const configThresholds = useDataTableConfigElement('rowThresholds');

  // check if either the table or any columns have thresholds
  const rowThresholds = props.rowThresholds ?? configThresholds;
  // First check if rowThresholds is defined, because this is the cheaper check
  if (rowThresholds) {
    options.rowThresholds = normalizeThresholdRules(rowThresholds);
    options.hasThresholds = true;
  } else {
    options.rowThresholds = undefined;
    // Check through the columnDefs if there are thresholds defined.
    options.hasThresholds = options.columns.some((column) => {
      if (isGroupColumnDef<TFeatures, Exclude<TData, unknown>>(column)) {
        const groupedColumn = column;
        return groupedColumn.columns?.some(
          (childColumn) =>
            childColumn.thresholds && childColumn.thresholds.length > 0,
        );
      }
      return column.thresholds && column.thresholds.length > 0;
    });
  }
}
